import type { ReportFilterOption } from '@reports/domain/types';

/**
 * ReportFilterOptions value object representing dynamic filter options from API
 *
 * Contains the actual selectable options for a specific filter.
 * This data is fetched from the API and can change over time.
 *
 * @param {string} type - Filter type (matches ReportFilterConfig.key)
 * @param {ReportFilterOption[]} optionValues - The actual selectable options for the filter
 *
 * @example
 * const filterOptions: ReportFilterOptions = {
 *   type: 'PROVIDER',
 *   optionValues: [
 *     { key: 'online', value: 'Pedidos online' },
 *     { key: 'parrot', value: 'Punto de venta' },
 *   ],
 * };
 */
export type ReportFilterOptions = Readonly<{
  type: string;
  optionValues: ReportFilterOption[];
}>;

/**
 * Factory function to create a new validated ReportFilterOptions instance
 *
 * @param {string} type - Filter type (matches ReportFilterConfig.key)
 * @param {ReportFilterOption[]} optionValues - The actual selectable options for the filter
 * @returns {ReportFilterOptions} The created report filter options
 *
 * @example
 * const filterOptions: ReportFilterOptions = createReportFilterOptions('PROVIDER', [
 *   { key: 'online', value: 'Pedidos online' },
 *   { key: 'parrot', value: 'Punto de venta' },
 */
export const createReportFilterOptions = (props: ReportFilterOptions): ReportFilterOptions => {
  if (!props.type || !props.optionValues) {
    throw new Error('Type and optionValues are required');
  }

  for (const option of props.optionValues) {
    if (!option.key || !option.value) {
      throw new Error('Option key and value are required');
    }
  }

  return Object.freeze(props);
};
